// ENG-027: one line above the chat input — "How did the assistant do in this conversation?"
// with Bad / Fine / Good and a close button. The choice is saved at once; "Bad" opens a second
// line with optional reasons and a one-line note that update the same feedback.

import React, { useEffect, useId, useRef, useState } from "react";
import type { FeedbackResponse } from "../../api/feedback";
import { getLocalizedText } from "../Example/ExampleCategories";
import { useExampleLanguage } from "../../contexts/ExampleLanguageContext";
import { RATING_LABEL_COPY, RATING_LABELS, REASON_TAG_COPY, REASON_TAGS, type RatingLabel, type ReasonTag } from "../../utils/ratingLabels";
import styles from "./RatingPrompt.module.css";

export interface RatingDetails {
    reasonTags: string[];
    comment: string;
}

interface RatingPromptProps {
    // Saves (or, with feedbackId, updates) the rating; resolves with the stored feedback.
    onRate: (label: RatingLabel, details?: RatingDetails, feedbackId?: string) => Promise<FeedbackResponse>;
    // The user picked a rating: the prompt must never come back for this session.
    onAnswered: () => void;
    // The user closed the prompt (×) or finished with it.
    onClose: () => void;
    onNotify: (message: string, type: "success" | "error") => void;
    isWidget?: boolean;
}

const COPY = {
    question: { el: "Πώς ήταν η βοήθεια σε αυτή τη συζήτηση;", en: "How did the assistant do in this conversation?" },
    groupLabel: { el: "Αξιολόγηση συζήτησης", en: "Conversation rating" },
    close: { el: "Κλείσιμο", en: "Close" },
    reasonsLabel: { el: "Τι πήγε στραβά;", en: "What went wrong?" },
    commentPlaceholder: { el: "Τι πήγε στραβά; (προαιρετικό)", en: "What went wrong? (optional)" },
    send: { el: "Αποστολή", en: "Send" },
    skip: { el: "Παράλειψη", en: "Skip" },
    thanks: { el: "Ευχαριστούμε!", en: "Thank you!" },
    saveError: { el: "Δεν αποθηκεύτηκε η αξιολόγηση.", en: "Could not save your rating." }
} as const;

const COMMENT_MAX_CHARS = 500;

export const RatingPrompt: React.FC<RatingPromptProps> = ({ onRate, onAnswered, onClose, onNotify, isWidget = false }) => {
    const { exampleLanguage } = useExampleLanguage();
    const t = (text: { el: string; en: string }) => getLocalizedText(text, exampleLanguage);
    const questionId = useId();
    const reasonsId = useId();
    const firstReasonRef = useRef<HTMLButtonElement | null>(null);
    const [stage, setStage] = useState<"choose" | "details">("choose");
    const [saving, setSaving] = useState(false);
    const [feedbackId, setFeedbackId] = useState<string | undefined>();
    const [reasons, setReasons] = useState<ReasonTag[]>([]);
    const [comment, setComment] = useState("");

    useEffect(() => {
        if (stage === "details") firstReasonRef.current?.focus();
    }, [stage]);

    const finish = () => {
        onNotify(t(COPY.thanks), "success");
        onClose();
    };

    const choose = async (label: RatingLabel) => {
        if (saving) return;
        setSaving(true);
        try {
            const response = await onRate(label);
            setFeedbackId(response.feedback_id);
            onAnswered();
            if (label === "bad") setStage("details");
            else finish();
        } catch {
            onNotify(t(COPY.saveError), "error");
        } finally {
            setSaving(false);
        }
    };

    const toggleReason = (tag: ReasonTag) => setReasons(prev => (prev.includes(tag) ? prev.filter(item => item !== tag) : [...prev, tag]));

    const sendDetails = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (saving) return;
        const note = comment.trim();
        if (!reasons.length && !note) {
            finish();
            return;
        }
        setSaving(true);
        try {
            await onRate("bad", { reasonTags: reasons, comment: note }, feedbackId);
            finish();
        } catch {
            onNotify(t(COPY.saveError), "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`${styles.bar} ${isWidget ? styles.widget : ""}`} role="group" aria-labelledby={questionId} data-print="hide">
            <div className={styles.row}>
                <span id={questionId} className={styles.question}>
                    {t(COPY.question)}
                </span>
                {stage === "choose" && (
                    <div className={styles.choices}>
                        {RATING_LABELS.map(label => (
                            <button key={label} type="button" className={styles.choice} onClick={() => void choose(label)} disabled={saving}>
                                {t(RATING_LABEL_COPY[label])}
                            </button>
                        ))}
                    </div>
                )}
                <button type="button" className={styles.close} onClick={onClose} aria-label={t(COPY.close)} title={t(COPY.close)}>
                    ×
                </button>
            </div>

            {stage === "details" && (
                <form className={styles.details} onSubmit={event => void sendDetails(event)}>
                    <div className={styles.reasons} role="group" aria-labelledby={reasonsId}>
                        <span id={reasonsId} className={styles.reasonsLabel}>
                            {t(COPY.reasonsLabel)}
                        </span>
                        {REASON_TAGS.map((tag, index) => (
                            <button
                                key={tag}
                                ref={index === 0 ? firstReasonRef : undefined}
                                type="button"
                                className={`${styles.reason} ${reasons.includes(tag) ? styles.reasonSelected : ""}`}
                                aria-pressed={reasons.includes(tag)}
                                onClick={() => toggleReason(tag)}
                                disabled={saving}
                            >
                                {t(REASON_TAG_COPY[tag])}
                            </button>
                        ))}
                    </div>
                    <div className={styles.noteRow}>
                        <input
                            type="text"
                            className={styles.note}
                            value={comment}
                            onChange={event => setComment(event.target.value)}
                            maxLength={COMMENT_MAX_CHARS}
                            placeholder={t(COPY.commentPlaceholder)}
                            aria-label={t(COPY.reasonsLabel)}
                            disabled={saving}
                        />
                        <button type="submit" className={styles.send} disabled={saving}>
                            {t(COPY.send)}
                        </button>
                        <button type="button" className={styles.skip} onClick={finish} disabled={saving}>
                            {t(COPY.skip)}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default RatingPrompt;
